import { formatEtherNumber, LSTIconMapping } from "@constants/common";
import { useReadContracts } from "wagmi";
import { useMemo } from "react";
import { BigNumber } from "ethers";
import useDappConfig from "./useDappConfig";
import { getImageWithSubRoute, numberToCurrency } from "utils";

function getTotalKelpMiles() {
  const BASE_VALUE = 13_994_404_951_388;
  const CHANGE_PER_HOUR = (30 * 1_000_000_000) / 24;
  const STARTING_TIME = Date.parse(
    "Mon Jun 23 2025 10:45:23 GMT+0530 (India Standard Time)"
  );

  const now = Date.now();
  const hoursSinceStart = (now - STARTING_TIME) / (1000 * 60 * 60);

  return BASE_VALUE + CHANGE_PER_HOUR * hoursSinceStart;
}

const functionNames = ["getTotalAssetDeposits", "getAssetDistributionData"];

export const useAssetRestaked = () => {
  const { config } = useDappConfig();
  const tokens = config.tokens;

  const tokenContractCalls = Object.keys(tokens).map((key) => {
    return functionNames.map((functionName) => ({
      ...config.interactionPools.lrtDepositPool,
      functionName,
      args: [tokens[key].address],
    }));
  });

  const nativeTokenCalls = functionNames.map((functionName) => ({
    ...config.interactionPools.lrtDepositPool,
    functionName,
    args: [config.nativeToken.address],
  }));

  const contractCalls = [...tokenContractCalls.flat(), ...nativeTokenCalls];

  const { data } = useReadContracts({
    contracts: contractCalls,
  });

  const assetsStakedStats = useMemo(() => {
    if (!data) return;

    type BN = BigNumber;

    const formatCur = (value: BN | null) => {
      return value
        ? numberToCurrency(formatEtherNumber(value.toString(), 1).toString(), 1)
        : "-";
    };

    const getAssetDistribution = (assetDistribution: [BN, BN, BN] | null) => {
      if (!assetDistribution || !assetDistribution.length) return;

      const [
        assetLyingInDepositPool,
        assetLyingInNDCs,
        assetStakedInEigenLayer,
      ] = assetDistribution.map((d) => BigNumber.from(d));

      const received = assetLyingInDepositPool
        .add(assetLyingInNDCs)
        .add(assetStakedInEigenLayer);

      const toBeStaked = assetLyingInDepositPool.add(assetLyingInNDCs);

      return {
        received: formatEtherNumber(received.toString(), 2).toLocaleString(),
        toBeStaked: formatEtherNumber(
          toBeStaked.toString(),
          2
        ).toLocaleString(),
        stakedInEigenLayer: formatEtherNumber(
          assetStakedInEigenLayer.toString(),
          2
        ).toLocaleString(),
      };
    };

    const filteredResponse = data.map((d) => d.result!);
    return [...Object.keys(tokens), config.nativeToken.symbol].map(
      (key, index) => {
        const assetsDepositIndex = index * 2;
        const distributionDetailsIndex = assetsDepositIndex + 1;
        return {
          title: key,
          value: formatCur(filteredResponse[assetsDepositIndex] as BN),
          icon: getImageWithSubRoute(LSTIconMapping[key]),
          assetDistribution: getAssetDistribution(
            filteredResponse[distributionDetailsIndex] as [BN, BN, BN]
          ),
        };
      }
    );
  }, [config.nativeToken.symbol, data, tokens]);

  return {
    assetsStakedStats: assetsStakedStats
      ?.filter((asset) => {
        return asset.title !== "sfrxETH";
      })
      .concat([
        {
          title: "KERNEL points",
          value: `${numberToCurrency(
            (getTotalKelpMiles() / 1000).toString(),
            1
          )}`,
          icon: getImageWithSubRoute(LSTIconMapping["KERNEL"]),
          assetDistribution: undefined,
        },
      ]),
  };
};
